import React, { useEffect, useState } from "react";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import s from "./Table.module.scss";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import dayjs from "dayjs";
import ReusablePopover from "../../../../../../global components/Popover/ReuseablePopover";
import clsx from "clsx";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import PopOverItems from "./PopOverItems/PopOverItems";
import Elipses from "../../../../../../global components/Icons/Elipses";
import ContactsGroup from "../../../../../../global components/Icons/ContactGroup/ContactsGroup";
import { UsersApiResponse } from "../../../../../../libs/Utils/Types/User";
import Unauthorised from "../../../../../../global components/Unauthorised";
import CsvDownloader from "../CsvDownloader";

type Prop = {
  filters: "" | "verified" | "pending" | "unverified";
};
function Table({ filters }: Prop) {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("");
  const [suspendedFilter, setSuspendedFilter] = useState<boolean | "">("");

  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const {
    data: TableUsers,
    isLoading,
    error,
    mutate,
  } = useSWR<UsersApiResponse, any, any>(
    token
      ? [
          "api/admin/users",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            ...(filter && { status: filter }),
            ...(suspendedFilter && { isSuspended: suspendedFilter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <div className={s.nameCell}>
          <span className={clsx(s.cell, s.name)}>{name}</span>
        </div>
      ),
    },
    {
      title: "Email address",
      dataIndex: "email",
      key: "email",
      render: (email: string) => <span className={clsx(s.cell)}>{email}</span>,
    },

    {
      title: "Groups",
      dataIndex: "groups",
      key: "groups",
      render: (groups: string) => (
        <span className={clsx(s.cell)}>{groups}</span>
      ),
    },
    {
      title: "Contacts",
      dataIndex: "contacts",
      key: "contacts",
      render: (contacts: string) => (
        <span className={clsx(s.cell)}>{contacts}</span>
      ),
    },
    {
      title: "Message sent",
      dataIndex: "msgSent",
      key: "msgSent",
      render: (msgSent: string) => (
        <span className={clsx(s.cell)}>{msgSent}</span>
      ),
    },

    {
      title: "Credit balance",
      dataIndex: "creditBal",
      key: "creditBal",
      render: (creditBal: string) => (
        <span className={clsx(s.cell)}>{creditBal}</span>
      ),
    },
    {
      title: "Date joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <span className={clsx(s.cell)}>
          {dayjs.utc(createdAt).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (
        status: string,
        record: { key: string; isSuspended: boolean },
      ) => (
        <span
          className={clsx(
            s.status,
            s.statusCell,
            status === "verified" && !record.isSuspended
              ? s.successful
              : status === "unverified"
                ? s.failed
                : record.isSuspended
                  ? s.failed
                  : s.pending,
          )}
        >
          {record.isSuspended
            ? "Suspended"
            : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },

    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (
        text: any,
        record: {
          key: string;
          isSuspended: boolean;
          name: string;
          status: string;
        },
      ) => (
        <ReusablePopover
          trigger={"click"}
          placement="rightBottom"
          content={
            <PopOverItems
              mutate={mutate}
              isSuspended={record.isSuspended}
              keyValue={record.key}
              name={record.name}
              status={record.status}
            />
          } // Pass the key value
        >
          <Elipses height={24} width={18} />
        </ReusablePopover>
      ),
    },
  ];
  // Transform API response to table data
  const transformedData = TableUsers?.data.map((user) => ({
    key: user.id,
    name: user.firstName + " " + user.lastName,
    email: user.email || "N/A",
    groups: user.noOfContactGroups || "0",
    contacts: user.noOfContacts || "0",
    creditBal: user.credits
      ? Number(user.credits).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : 0,
    msgSent: user.noOfMessages || "0",
    isSuspended: user.isSuspended || false,
    status: user.verificationStatus || "N/A",
    createdAt: user.createdAt,
    actions: "", // You can customize actions here as needed
  }));

  // Handle error state

  // const rowSelection: TableProps<DataType>["rowSelection"] = {
  //   onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {},
  //   getCheckboxProps: (record: DataType) => ({
  //     // disabled: record.name === "Disabled User", // Example conditional disable logic
  //     // name: record.name,
  //   }),
  // };
  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
  function handleSuspendedFilter(value: boolean | "") {
    setSuspendedFilter(value);
  }
  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  useEffect(() => {
    if (filters) {
      setFilter(filters);
    }
  }, [filters]);
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message: e.message || "Something went wrong",
        });
      }
    }
  }, [error]);
  return (
    <div className={s.wrapper}>
      <div className={s.tableWrapper}>
        <div className={s.formWrapper}>
          <div className={s.params}>
            <div className={s.searchInput}>
              <SearchInput
                style={{ width: "100%", height: "100%" }}
                placeholder="search"
                width={332}
                height={45}
                handleSearch={handleSearch}
              />
            </div>
            <SelectField
              bordered
              label={"Status:"}
              defaultValue={""}
              wrapperClassname={s.SelectWrapper}
              className={clsx(s.selectInput)}
              value={filter}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "verified", label: "Verified" },
                { value: "pending", label: "Pending" },
                { value: "unverified", label: "Unverified" },
              ]}
            />
            <SelectField
              bordered
              label={"Account State:"}
              defaultValue={"all"}
              wrapperClassname={s.SelectWrapper}
              className={clsx(s.selectInput)}
              onChange={(value: any) => handleSuspendedFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "true", label: "Suspended" },
                { value: "false", label: "Unsuspended" },
              ]}
            />
          </div>
          <div className={s.params}>
            <Paginate
              onChange={handlePageChange}
              total={TableUsers?.pagination.totalItems || 0}
              pageSize={pageItems}
            />
            <CsvDownloader
              token={token!}
              filter={filter}
              endpointKey="api/admin/users"
              filename="Users"
              limit={pageItems}
            />
          </div>
        </div>
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            unauthorised ? (
              typeof unauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={unauthorised} />
              )
            ) : (
              <NoData
                Message="No user yet"
                subMessage=""
                icon={<ContactsGroup fill="#F0EFEE" height={60} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
          //rowSelection={rowSelection} // Include row selection prop
        />
      </div>
    </div>
  );
}
export default Table;
